import { IScope, IQService, IPromise, IRootScopeService } from "angular";
import { StateService, StateParams } from "@uirouter/angularjs";
import { IInsightboard, IInsightboardPermissions } from "../models/models";
import { InsightboardService } from "../services/dashboardService";
import { PermissionsService } from "@gtmhub/users/permissionsService";
import { ICheckAccessFactory } from "@gtmhub/shared/factories/check-permissions";
import { IIndicator, UIErrorHandlingService } from "@gtmhub/error-handling";
import { IAccessChecked } from "@gtmhub/shared/models";
import { IStateInit } from "@gtmhub/core/routing";
import { IAccess, IApiPermission, IUserItemAllowedActions } from "@gtmhub/sessions";
import { IDynamicSubteamsRole, RoleService } from "@gtmhub/roles";

export type EditInsightboardIndicators = {
  cloning?: IIndicator;
  loading?: IIndicator;
  saving?: IIndicator;
};

export interface IEditInsightboardScope extends IScope {
  indicators: EditInsightboardIndicators;
  insightboard: IInsightboard;
  originalInsightboard: IInsightboard;
  permissionsType: {
    custom: boolean;
    public: boolean;
  };
  permissions: IInsightboardPermissions;
  stateParams: StateParams;
  changePermissionsType(type: string): void;
  save(): void;
  resetIndicator(indicator: keyof EditInsightboardIndicators): void;
}

export class EditInsightboardCtrl implements IStateInit {
  public static $inject = ["$rootScope", "$scope", "$state", "$q", "InsightboardService", "PermissionsService", "CheckAccess", "UIErrorHandlingService", "RoleService"];

  constructor(
    private $rootScope: IRootScopeService,
    private $scope: IEditInsightboardScope,
    private $state: StateService,
    private $q: IQService,
    private insightboardService: InsightboardService,
    private permissionsService: PermissionsService,
    private checkAccess: ICheckAccessFactory,
    private uiErrorHandlingService: UIErrorHandlingService,
    private roleService: RoleService
  ) {}

  public stateInit(): IPromise<unknown> {
    return this.init();
  }

  private strings = {
    update: "update",
    modifyPermissions: "modifyPermissions",
    insightBoard: "insightBoard",
    public: "public",
    custom: "custom",
  };

  private init = (): IPromise<unknown> => {
    this.$scope.indicators = {};

    this.$scope.stateParams = this.$state.params;

    this.$scope.save = this.save;
    this.$scope.changePermissionsType = this.changePermissionsType;
    this.$scope.resetIndicator = this.resetIndicator;

    this.$scope.indicators.loading = { progress: true };

    const promiseMap: { [id: string]: IPromise<unknown> } = {
      actions: this.permissionsService.getCurrentUserAllowedActionsPerItem(this.$state.params.editDashboardId, this.strings.insightBoard),
      insightboard: this.insightboardService.getInsightboard(this.$state.params.editDashboardId).then((insightboard: IInsightboard) => {
        return this.processDynamicSubteamsPermissions(insightboard).then(() => insightboard);
      }),
    };

    return this.$q.all(promiseMap).then(
      (resolvedMap: { actions: IUserItemAllowedActions; insightboard: IInsightboard }) => {
        this.resetIndicator("loading");

        this.$scope.insightboard = resolvedMap.insightboard;
        this.$scope.originalInsightboard = angular.copy(this.$scope.insightboard);

        this.$scope.permissionsType = {
          custom: this.$scope.originalInsightboard.access.inherits ? false : true,
          public: this.$scope.originalInsightboard.access.inherits ? true : false,
        };

        let allowedActionsMap: { [action: string]: boolean } = {};
        allowedActionsMap = resolvedMap.actions.currentUserActions.reduce(
          (
            map: { [action: string]: boolean },
            currentAction: string
          ): {
            [action: string]: boolean;
          } => {
            map[currentAction] = true;

            return map;
          },
          allowedActionsMap
        );

        this.$scope.permissions = {
          form: allowedActionsMap[this.strings.update] || allowedActionsMap[this.strings.modifyPermissions] ? true : false,
          modifyName: allowedActionsMap[this.strings.update] ? true : false,
          modifyPermissions: allowedActionsMap[this.strings.modifyPermissions] ? true : false,
        };
        setTimeout(() => document.getElementById("insightboard_name").focus());
      },
      (error) => (this.$scope.indicators.loading = { error })
    );
  };

  private changePermissionsType = (type: string): void => {
    switch (type) {
      case this.strings.custom:
        this.$scope.insightboard.access.inherits = false;

        this.$scope.permissionsType = {
          custom: true,
          public: false,
        };
        break;

      case this.strings.public:
        this.$scope.insightboard.access = angular.copy(this.$scope.originalInsightboard.access);

        this.$scope.permissionsType = {
          public: true,
          custom: false,
        };
        break;

      default:
        break;
    }
  };

  private save = (): void => {
    this.checkAccess.checkAccess(this.$scope.insightboard.access, angular.copy(this.$scope.originalInsightboard.access)).then((accessChecked: IAccessChecked) => {
      if (accessChecked.canSave) {
        this.$scope.insightboard.access = this.includeSubteamsPermissions(accessChecked.access);

        if (this.$state.params.toBeCloned) {
          this.cloneInsightboard();
        } else {
          this.editInsightboard();
        }
      }
    });
  };

  private cloneInsightboard = () => {
    const newBoardTag: string = Math.random().toString(36).substr(2, 10);

    this.$scope.indicators.cloning = { progress: true };

    // we need to first clone the old insightboard and then update the cloned one with the edited attributes
    this.insightboardService.cloneInsightboard(this.$scope.insightboard.id, newBoardTag).then(
      (newInsightboard: IInsightboard) => {
        this.resetIndicator("cloning");

        if (this.$scope.originalInsightboard && this.$scope.originalInsightboard.name !== this.$scope.insightboard.name) {
          newInsightboard.name = this.$scope.insightboard.name;
        }
        newInsightboard.access = this.$scope.insightboard.access;
        this.editInsightboard(newInsightboard);
      },
      (error) => (this.$scope.indicators.cloning = { error })
    );
  };

  private editInsightboard = (insightboard?: IInsightboard): void => {
    this.$scope.indicators.saving = { progress: true };

    const insightboardToUpdate: IInsightboard = insightboard ? insightboard : this.$scope.insightboard;

    this.insightboardService.updateInsightboard(insightboardToUpdate).then(
      (editedInsightboard: IInsightboard) => {
        this.$scope.resetIndicator("saving");

        this.$rootScope.$broadcast("insightboardEdited", editedInsightboard);

        if (this.$state.params.redirectToClonedOnSave) {
          // goes back to insightboard view with cloned insightboard
          this.$state.go("^.^", { dashboardId: editedInsightboard.id });
        } else {
          // goes back to insightboards view with all insightboards
          this.$state.go("^.^");
        }
      },
      (err) => this.handleIndicatorError(err, "saving", true)
    );
  };

  private handleIndicatorError = <E>(err: E, indicator: string, inline = false): void => {
    this.uiErrorHandlingService.handleIndicator(err, { indicator, inline });
  };

  private processDynamicSubteamsPermissions = (insightboard: IInsightboard): IPromise<void[]> => {
    const promises: IPromise<void>[] = [];
    if (insightboard.access.permissions) {
      insightboard.access.permissions.forEach((p) => {
        if (p.principalKind === "team_and_subteams") {
          promises.push(this.setCorrectValuesToPermission(p));
        }
      });
    }
    return this.$q.all(promises);
  };

  private includeSubteamsPermissions = (access: IAccess): IAccess => {
    if (access == null) {
      return access;
    }

    const newAccess = angular.copy(access);
    const parentsWithIncludedSubteams = this.getTeamsPermissionsWithIncludedSubteams(newAccess);
    if (parentsWithIncludedSubteams.length === 0) {
      return access;
    }
    parentsWithIncludedSubteams.forEach((p) => (p.principalKind = "team_and_subteams"));

    if (newAccess.permissions.length > 0) {
      newAccess.inherits = false;
    }

    return newAccess;
  };

  private getTeamsPermissionsWithIncludedSubteams = (access: IAccess) => {
    return access.permissions.filter((permission) => {
      return permission.principalKind === "team" && permission.includeSubteams;
    });
  };

  private setCorrectValuesToPermission(permission: IApiPermission): IPromise<void> {
    const deferred = this.$q.defer<void>();

    this.roleService
      .getRole(permission.principalId)
      .then((response: IDynamicSubteamsRole) => {
        permission.principalId = response.teamId;
        permission.principalKind = "team";
        permission.includeSubteams = true;
        deferred.resolve();
      })
      .catch((err) => {
        if (err.status === 404) {
          // Ignoring 404. It could receive 404 when team is deleted.
          deferred.resolve();
        } else {
          deferred.reject(err);
        }
      });

    return deferred.promise;
  }

  private resetIndicator = (indicator: keyof EditInsightboardIndicators): void => {
    delete this.$scope.indicators[indicator];
  };
}
